import React, { useState } from 'react';
import { gql, useMutation } from '@common/hooks';
import { Divider, Switch, Typography } from 'antd';
import { createMessage } from 'works/legacy/lib/message-helper';
import { NotificationType } from '@identity/types';
import { Colors, FontSize, FontWeight } from '@common/tokens';
import { Text } from '@common/components';
import { DisableAllNotificationsConfirmModal } from './disable-all-notifications-confirm-modal';
import { Notification } from '@identity/pages/account-settings/notifications/notification';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';

type NotificationsSectionProps = {
  allowEmailNotifications: boolean;
  notificationCategories?: {
    key: string;
    name: string;
  }[];
  notifications: NotificationType[];
};

type ActionType = {
  notifications: NotificationType[];
  enabled: boolean;
};

const updateMembershipNotificationPreference = gql`
  mutation UpdateMembershipNotificationPreference(
    $input: UpdateMembershipNotificationsInput!
  ) {
    updateMembershipNotificationPreference(input: $input) {
      success
    }
  }
`;

function filterNotificationsByCategory(
  notifications: NotificationType[],
  categoryName: string
) {
  return notifications.filter(
    (notification) => notification.category === categoryName
  );
}

export function NotificationsSection({
  allowEmailNotifications,
  notificationCategories,
  notifications,
}: NotificationsSectionProps) {
  useSetBreadcrumbs([
    {
      name: 'Account Settings',
      path: '/works/account-settings',
    },
    {
      name: 'Notifications',
      path: '/works/account-settings/notifications',
    },
  ]);

  const [showSwitchOffModal, setShowSwitchOffModal] = useState(false);
  const [updateNotificationsMutation] = useMutation(
    updateMembershipNotificationPreference,
    {
      refetchQueries: ['accountSettingsNotificationsQuery'],
      onError: () => {
        createMessage(
          'error',
          'Failed to save notification settings. Please try again.'
        );
      },
    }
  );

  function updateNotifications(action: ActionType) {
    updateNotificationsMutation({
      variables: {
        input: {
          allowEmailNotifications: allowEmailNotifications,
          notifications: action.notifications.map(
            (notification: NotificationType) => ({
              notificationName: notification.notificationName,
              enabled: action.enabled,
            })
          ),
        },
      },
    });
  }

  function handleGlobalSwitch(action: ActionType) {
    updateNotificationsMutation({
      variables: {
        input: {
          allowEmailNotifications: action.enabled,
          notifications: action.notifications.map(
            (notification: NotificationType) => ({
              notificationName: notification.notificationName,
              enabled: action.enabled,
            })
          ),
        },
      },
    });
  }

  function onGlobalSwitchChange(enabled: boolean) {
    if (enabled) {
      handleGlobalSwitch({
        notifications: notifications,
        enabled: true,
      });
    } else {
      setShowSwitchOffModal(true);
    }
  }

  function onModalCancel() {
    setShowSwitchOffModal(false);
  }

  function onModalConfirm() {
    handleGlobalSwitch({
      notifications: notifications,
      enabled: false,
    });
    setShowSwitchOffModal(false);
  }

  return (
    <section className="account-settings__notifications-section">
      <Typography.Title level={4} style={{ fontSize: FontSize.lg }}>
        Notifications
      </Typography.Title>
      <Text block secondary>
        Manage your email notification preferences below. Mobile push
        notifications are configured separately through the Works mobile app.
      </Text>
      <Divider />
      {!notifications.length ? (
        <p>Your account does not have email notifications.</p>
      ) : (
        <>
          <div className="account-settings__notifications-section__global-switch">
            <Switch
              checked={allowEmailNotifications}
              onChange={onGlobalSwitchChange}
            />
            <span className="account-settings__notifications-section__global-switch__name">
              <Text bold>Enable Email Notifications</Text>
            </span>
          </div>
          {notificationCategories?.map((category) => {
            const notificationsFilteredByCategory =
              filterNotificationsByCategory(notifications, category.key);
            const hasActiveChildren = notificationsFilteredByCategory.some(
              ({ enabledForCurrentMembership }: NotificationType) =>
                Boolean(enabledForCurrentMembership)
            );

            return (
              <>
                <Divider />
                <div className="account-settings__notifications-section__category">
                  <Switch
                    key={category.key}
                    checked={hasActiveChildren}
                    onChange={(enabled) =>
                      updateNotifications({
                        notifications: notificationsFilteredByCategory,
                        enabled,
                      })
                    }
                  />
                  <span className="account-settings__notifications-section__category__switch__name">
                    {category.name}
                  </span>
                </div>
                {notifications.map(
                  (notification) =>
                    notification.category === category.key && (
                      <Notification
                        key={notification.notificationName}
                        notification={notification}
                        updateNotifications={updateNotifications}
                      />
                    )
                )}
              </>
            );
          })}
        </>
      )}
      <DisableAllNotificationsConfirmModal
        visible={showSwitchOffModal}
        onCancel={onModalCancel}
        onConfirm={onModalConfirm}
      />
      <style jsx>{`
        .account-settings__notifications-section :global(.ant-typography) {
          margin: 24px 0;
          font-weight: ${FontWeight.medium};
        }

        .account-settings__notifications-section__global-switch {
          display: flex;
          align-items: center;
        }

        .account-settings__notifications-section__global-switch
          :global(.ant-switch) {
          margin-right: 8px;
        }

        .account-settings__notifications-section__global-switch__name {
          font-weight: ${FontWeight.medium};
          font-size: ${FontSize.md};
          margin: 0;
        }

        .account-settings__notifications-section__category {
          margin: 24px 0;
          font-weight: ${FontWeight.medium};
          color: ${Colors.textDefault};
          font-size: ${FontSize.md};
          display: flex;
          align-items: center;
        }

        .account-settings__notifications-section__category
          :global(.ant-switch) {
          margin-right: 8px;
        }

        .account-settings__notifications-section__category__switch__name {
          font-weight: ${FontWeight.medium};
          color: ${Colors.textDefault};
          font-size: ${FontSize.md};
        }
      `}</style>
    </section>
  );
}
